/// <amd-module name="Repos/Views/PullRequest/Components/ActivityFeed/Utils/ActivityFeedUtils" />
import * as React from "react";
import { IFileCharDiffBlock, IFileDiff } from "Repos/ChangesCommon/Contracts";
import { CommentThreadContext, GitPullRequestCommentThread } from "Repos/Common/Contracts/Git";
import { IBasicIdentityRef, Persona, PersonaSize } from "VSSUI/Persona";
import { Tooltip } from "VSSUI/TooltipEx";

export function getPropertyValue(objWithProps: GitPullRequestCommentThread, propName: string, defaultValue: string = ""): string {
    if (objWithProps.properties[propName]) {
        return objWithProps.properties[propName].$value;
    }
    return defaultValue;
}

export function getIdentity(thread: GitPullRequestCommentThread, id: string): IBasicIdentityRef | undefined {
    const identityIndex = parseInt(id, 10);
    return !isNaN(identityIndex) ? thread.identities[identityIndex] : undefined;
}

export function getIdentityComponent(identity: IBasicIdentityRef, personaClassName: string) {
    return (
        <>
            <Persona ariaHidden={true} className={personaClassName} identity={identity} size={PersonaSize.size24} />
            <Tooltip overflowOnly={true}>
                <span className="text-ellipsis">{identity.displayName}</span>
            </Tooltip>
        </>
    );
}

export interface IThreadDiffResult {
    diff: IFileDiff;
    hasLinesAfter: boolean;
    hasLinesBefore: boolean;
}

export function getDiffForThread(
    fileDiff: IFileDiff,
    threadContext: CommentThreadContext,
    extraLinesAfter: number,
    extraLinesBefore: number
): IThreadDiffResult | undefined {
    const blockToOverallLineStart: number[] = [];
    let overallLineCount = 0;

    let overallLineStart = 0;
    let overallLineEnd = 0;

    // Do a pass to compute:
    // - The first and last overall line number that we want to show
    // - Construct the map from each block to its starting overall line number
    for (let i = 0, l = fileDiff.lineCharBlocks.length; i < l; i++) {
        const lineChange = fileDiff.lineCharBlocks[i].lineChange;
        blockToOverallLineStart[i] = overallLineCount + 1;

        if (!overallLineStart) {
            if (threadContext.rightFileStart) {
                if (
                    lineChange.mLine &&
                    threadContext.rightFileStart.line >= lineChange.mLine &&
                    threadContext.rightFileStart.line <= lineChange.mLine + lineChange.mLinesCount - 1
                ) {
                    overallLineStart = overallLineCount + 1 + threadContext.rightFileStart.line - lineChange.mLine;
                }
            } else if (threadContext.leftFileStart) {
                if (
                    lineChange.oLine &&
                    threadContext.leftFileStart.line >= lineChange.oLine &&
                    threadContext.leftFileStart.line <= lineChange.oLine + lineChange.oLinesCount - 1
                ) {
                    overallLineStart = overallLineCount + 1 + threadContext.leftFileStart.line - lineChange.oLine;
                }
            }
        }

        if (!overallLineEnd) {
            if (threadContext.rightFileEnd) {
                if (
                    lineChange.mLine &&
                    threadContext.rightFileEnd.line >= lineChange.mLine &&
                    threadContext.rightFileEnd.line <= lineChange.mLine + lineChange.mLinesCount - 1
                ) {
                    overallLineEnd = overallLineCount + 1 + threadContext.rightFileEnd.line - lineChange.mLine;
                }
            } else if (threadContext.leftFileEnd) {
                if (
                    lineChange.oLine &&
                    threadContext.leftFileEnd.line >= lineChange.oLine &&
                    threadContext.leftFileEnd.line <= lineChange.oLine + lineChange.oLinesCount - 1
                ) {
                    overallLineEnd = overallLineCount + 1 + threadContext.leftFileEnd.line - lineChange.oLine;
                }
            }
        }

        overallLineCount += Math.max(lineChange.oLinesCount, lineChange.mLinesCount);
    }

    if (overallLineStart === 0 && overallLineEnd === 0) {
        return undefined;
    }

    overallLineStart = Math.max(1, overallLineStart - extraLinesBefore);
    overallLineEnd = Math.min(overallLineCount, overallLineEnd + extraLinesAfter);

    const newBlocks: IFileCharDiffBlock[] = [];

    // Do a second pass
    // - Include diff blocks in between the start and end
    // - Trim lines from the beginning of blocks that span the start line
    // - Trim lines from the end of blocks that span the end line
    for (let i = 0, l = fileDiff.lineCharBlocks.length; i < l; i++) {
        let block = fileDiff.lineCharBlocks[i];

        let blockStart = blockToOverallLineStart[i];
        let blockLength = Math.max(block.lineChange.mLinesCount, block.lineChange.oLinesCount);
        const blockEnd = blockStart + blockLength - 1;

        if (blockEnd >= overallLineStart && blockStart <= overallLineEnd) {
            // Truncate starting lines
            if (blockStart < overallLineStart) {
                block = { ...block, lineChange: { ...block.lineChange } };
                block.lineChange.mLines = block.lineChange.mLines || [];
                block.lineChange.oLines = block.lineChange.oLines || [];

                const linesToRemove = overallLineStart - blockStart;

                block.lineChange.mLines = block.lineChange.mLines.slice(linesToRemove);
                block.lineChange.mLine += block.lineChange.mLinesCount - block.lineChange.mLines.length;
                block.lineChange.mLinesCount = block.lineChange.mLines.length;
                block.lineChange.oLines = block.lineChange.oLines.slice(linesToRemove);
                block.lineChange.oLine += block.lineChange.oLinesCount - block.lineChange.oLines.length;
                block.lineChange.oLinesCount = block.lineChange.oLines.length;

                blockStart += linesToRemove;
                blockLength -= linesToRemove;
            }

            // Truncate ending lines
            if (blockEnd > overallLineEnd) {
                block = { ...block, lineChange: { ...block.lineChange } };
                block.lineChange.mLines = block.lineChange.mLines || [];
                block.lineChange.oLines = block.lineChange.oLines || [];

                const linesToKeep = overallLineEnd - blockStart + 1;

                block.lineChange.mLines = block.lineChange.mLines.slice(0, linesToKeep);
                block.lineChange.mLinesCount = block.lineChange.mLines.length;
                block.lineChange.oLines = block.lineChange.oLines.slice(0, linesToKeep);
                block.lineChange.oLinesCount = block.lineChange.oLines.length;
            }

            newBlocks.push(block);
        }
    }

    return {
        diff: {
            ...fileDiff,
            lineCharBlocks: newBlocks
        },
        hasLinesAfter: overallLineEnd < overallLineCount,
        hasLinesBefore: overallLineStart > 1
    };
}
